import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Chip, Divider, Grid, Stack, Typography, styled } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Flex, colors } from "@zfp-akademie-a-s/zfp-design";
import { FormDatePicker } from "@zfp-akademie-a-s/zfp-design/form/date-picker";
import { NumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import {
  getGetAssetsOverviewQueryKey,
  getListInsuranceFamilyLiabilitiesQueryKey,
  getListLiabilityItemsQueryKey,
  useGetAnalysisClients,
  useGetClientInsuranceRecapitulation,
  useListInsuranceFamilyLiabilities,
  useListLiabilityItems,
  useUpdateLiabilityItem,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, AnalysisClientItemAgeCategoryType } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { LIABILITY_SUBTYPE_RESOURCE_KEY } from "Infrastructure/Utils/AssetUtils";
import { formatCurrencyWithoutCode } from "Infrastructure/Utils/CurrencyUtils";
import { FAMILY_EXPENSE_TYPE_RESOURCE_KEY, INCOME_LINE_TYPE_RESOURCE_KEY } from "Infrastructure/Utils/InsuranceUtils";
import { inflectChildren } from "Infrastructure/Utils/LanguageUtils";
import { differenceInYears, format, isValid, parseISO } from "date-fns";
import { type FC, useContext, useEffect } from "react";
import React from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as yup from "yup";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";

type Props = {
  analysisId: string;
  versionId: string;
  clientId?: string;
};

type LiabilityDueDateFormData = {
  dueDate?: Date | null;
};

const getStartOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const getLiabilityDueDateSchema = (t: ReturnType<typeof useResource>["t"]) =>
  yup.object({
    dueDate: yup
      .date()
      .typeError(t(Resources.Validation.BadValue))
      .min(getStartOfToday(), t(Resources.Validation.DateMustNotBeInPast))
      .nullable(),
  });

const LiabilityDueDatePicker: FC<{
  dueDate?: string | null;
  disabled: boolean;
  label: string;
  onChange: (date: Date | null) => void;
}> = ({ dueDate, disabled, label, onChange }) => {
  const { t } = useResource();
  const { control, reset, trigger, getValues } = useForm<LiabilityDueDateFormData>({
    resolver: yupResolver(getLiabilityDueDateSchema(t)),
    defaultValues: { dueDate: dueDate ? parseISO(dueDate) : null },
  });

  useEffect(() => {
    reset({ dueDate: dueDate ? parseISO(dueDate) : null });
  }, [dueDate, reset]);

  const handleBlur = async () => {
    const isFieldValid = await trigger("dueDate");
    if (!isFieldValid) return;

    const rawValue = getValues("dueDate");
    const parsedValue = typeof rawValue === "string" ? parseISO(rawValue) : rawValue;
    if (parsedValue == null) {
      onChange(null);
      return;
    }
    if (isValid(parsedValue)) {
      onChange(parsedValue);
    }
  };

  return (
    <FormDatePicker
      control={control}
      name="dueDate"
      label={label}
      disablePast
      disabled={disabled}
      onBlur={handleBlur}
      slotProps={{ textField: { fullWidth: true } }}
    />
  );
};

const StyledStack = styled(Stack)`
  background: ${colors.backgroundSecondary};
  padding: ${(props) => props.theme.spacing(Spacing.XL)};
  border-radius: ${(props) => props.theme.spacing(Radius.XL)};
`;

export const AdditionalInformation: FC<Props> = ({ analysisId, versionId, clientId }) => {
  const queryClient = useQueryClient();
  const { readOnly } = useContext(AnalysisDetailContext);
  const { t } = useResource();

  const { data: familyRecapitulation } = useGetClientInsuranceRecapitulation(analysisId, versionId, clientId ?? "", {
    query: { enabled: !!clientId },
  });
  const { data: familyLiabilities } = useListInsuranceFamilyLiabilities(analysisId, versionId);
  // The insurance family-liabilities overview doesn't carry dueDate/note/institution/relatedExpenseItemGuid —
  // fetch the full liability items once to fill those in when saving the repayment date below.
  const { data: liabilityItems } = useListLiabilityItems(analysisId, versionId);
  const liabilityItemsByGuid = new Map(liabilityItems?.map((item) => [item.guid, item]));

  const { data: clientRelationsData } = useGetAnalysisClients(analysisId, versionId, {
    query: {
      staleTime: 20 * 60 * 1000,
    },
  });

  const { mutateAsync: updateLiability } = useUpdateLiabilityItem({
    mutation: {
      onSuccess: () => {
        toast.success(t(Resources.AnalysisDetail.Assets.Liabilities.Edited));
        queryClient.invalidateQueries({ queryKey: getListInsuranceFamilyLiabilitiesQueryKey(analysisId, versionId) });
        queryClient.invalidateQueries({ queryKey: getGetAssetsOverviewQueryKey(analysisId, versionId) });
        queryClient.invalidateQueries({ queryKey: getListLiabilityItemsQueryKey(analysisId, versionId) });
      },
    },
  });

  const childrenCount = clientRelationsData?.clients.filter(
    (c) => differenceInYears(new Date(), c.birthDate ? new Date(c.birthDate) : new Date()) < 18,
  ).length;
  const primaryIncomeIndex = familyRecapitulation?.clientIncomes.reduce(
    (iMax, x, i, arr) => (x.amount.value > arr[iMax].amount.value ? i : iMax),
    0,
  );

  const isAdult =
    clientRelationsData?.clients.find((client) => client.guid === clientId)?.ageCategoryType === AnalysisClientItemAgeCategoryType.adult;
  const isIndividual =
    clientRelationsData?.clients.filter(
      (x) => x.ageCategoryType === AnalysisClientItemAgeCategoryType.adult || x.ageCategoryType === AnalysisClientItemAgeCategoryType.child,
    ).length === 1;

  return (
    <Stack spacing={Spacing.L}>
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>
          {t(Resources.AnalysisDetail.Insurance.AdditionalInformation.Title)}
        </Typography>
        <Chip
          size="small"
          color="info"
          label={
            !isAdult
              ? t(Resources.AnalysisDetail.Insurance.AdditionalInformation.ChildChip)
              : isIndividual
                ? t(Resources.AnalysisDetail.Insurance.AdditionalInformation.IndividualChip)
                : t(Resources.AnalysisDetail.Insurance.AdditionalInformation.BreadwinnerChip)
          }
        />
      </Box>

      <Stack spacing={Spacing.M}>
        {isAdult && (
          <Box display="flex" justifyContent="space-between" alignItems="center" height={32}>
            <Typography fontWeight={500} variant="body2">
              {t(Resources.AnalysisDetail.Insurance.AdditionalInformation.ChildrenCount)}
            </Typography>
            <Box display="flex">
              <Typography variant="body2" fontWeight={500}>
                {childrenCount}
              </Typography>
              <Typography variant="body2" color={colors.textSecondary}>
                &nbsp;{inflectChildren(childrenCount ?? 0, t)}
              </Typography>
            </Box>
          </Box>
        )}
        <Divider />
        <Box display="flex" justifyContent="space-between" alignItems="center" height={32}>
          <Typography fontWeight={500} variant="body2">
            {t(Resources.AnalysisDetail.Insurance.AdditionalInformation.FamilyNetIncome)}
          </Typography>
          <Box display="flex">
            <Typography variant="body2" fontWeight={500}>
              {formatCurrencyWithoutCode(familyRecapitulation?.familyIncome.value)}
            </Typography>
            <Typography variant="body2" color={colors.textSecondary}>
              &nbsp;{t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
            </Typography>
          </Box>
        </Box>
        {isAdult && <Divider />}
        <Stack spacing={Spacing.XS}>
          {isAdult && (
            <Box display="flex" justifyContent="space-between" alignItems="center" height={32}>
              <Typography fontWeight={500} variant="body2">
                {t(Resources.AnalysisDetail.Insurance.AdditionalInformation.NetIncomeInsured)}
              </Typography>
              <Box display="flex">
                <Typography variant="body2" fontWeight={500}>
                  {formatCurrencyWithoutCode(familyRecapitulation?.clientIncomes.reduce((prev, curr) => prev + curr.amount.value, 0))}
                </Typography>
                <Typography variant="body2" color={colors.textSecondary}>
                  &nbsp;{t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                </Typography>
              </Box>
            </Box>
          )}
          {familyRecapitulation?.clientIncomes.map((income, index) => {
            return (
              <Box display="flex" justifyContent="space-between" alignItems="center" height={32} key={`${income.name}-${index}`}>
                <Flex alignItems="center">
                  <Typography variant="body2">
                    {t(
                      Resources.AnalysisDetail.Insurance.AdditionalInformation.IncomeTypes[
                        INCOME_LINE_TYPE_RESOURCE_KEY[income.incomeType]
                      ],
                    )}
                  </Typography>
                  {primaryIncomeIndex === index && (
                    <Chip
                      size="small"
                      color="info"
                      label={t(Resources.AnalysisDetail.Insurance.AdditionalInformation.MainIncomeChip)}
                      sx={{ ml: Spacing.S }}
                    />
                  )}
                </Flex>
                <Box display="flex">
                  <Typography variant="body2">{formatCurrencyWithoutCode(income.amount.value)}</Typography>
                  <Typography variant="body2" color={colors.textSecondary}>
                    &nbsp;{t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
        <Divider />
        <Stack spacing={Spacing.XS}>
          <Box display="flex" justifyContent="space-between" alignItems="center" height={32}>
            <Typography fontWeight={500} variant="body2">
              {t(Resources.AnalysisDetail.Insurance.AdditionalInformation.FamilyExpenses)}
            </Typography>
            <Box display="flex">
              <Typography variant="body2" fontWeight={500}>
                {formatCurrencyWithoutCode(familyRecapitulation?.familyExpenses.reduce((prev, curr) => prev + curr.amount.value, 0))}
              </Typography>
              <Typography variant="body2" color={colors.textSecondary}>
                &nbsp;{t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
              </Typography>
            </Box>
          </Box>
          {familyRecapitulation?.familyExpenses.map((expense, index) => {
            return (
              <Box key={`${expense.expenseType}-${index}`} display="flex" justifyContent="space-between" alignItems="center" height={32}>
                <Typography variant="body2">
                  {t(
                    Resources.AnalysisDetail.Insurance.AdditionalInformation.ExpenseTypes[
                      FAMILY_EXPENSE_TYPE_RESOURCE_KEY[expense.expenseType]
                    ],
                  )}
                </Typography>
                <Box display="flex">
                  <Typography variant="body2">{formatCurrencyWithoutCode(expense.amount.value)}</Typography>
                  <Typography variant="body2" color={colors.textSecondary}>
                    &nbsp;{t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
        {isAdult && (
          <>
            <Divider />
            <Box display="flex" justifyContent="space-between" alignItems="center" height={32}>
              <Typography fontWeight={500} variant="body2">
                {t(Resources.AnalysisDetail.Insurance.AdditionalInformation.InsureeExpenses)}
              </Typography>
              <Box display="flex">
                <Typography variant="body2" fontWeight={500}>
                  {formatCurrencyWithoutCode(familyRecapitulation?.clientExpense.value)}
                </Typography>
                <Typography variant="body2" color={colors.textSecondary}>
                  &nbsp;{t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                </Typography>
              </Box>
            </Box>
          </>
        )}

        <Divider />
        <Box display="flex" justifyContent="space-between" alignItems="center" height={32}>
          <Typography fontWeight={500} variant="body2">
            {t(Resources.AnalysisDetail.Insurance.AdditionalInformation.FamilyLiabilities)}
          </Typography>
          <Box display="flex">
            <Typography variant="body2" fontWeight={500}>
              {formatCurrencyWithoutCode(familyLiabilities?.reduce((prev, curr) => prev + curr.paymentAmount.value, 0))}
            </Typography>
            <Typography variant="body2" color={colors.textSecondary}>
              &nbsp;{t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
            </Typography>
          </Box>
        </Box>
        <StyledStack spacing={Spacing.L}>
          {familyLiabilities?.map((liability, index) => {
            const dueDate = liabilityItemsByGuid.get(liability.liability.guid)?.dueDate;

            return (
              <React.Fragment key={liability.liability.guid}>
                <Box display="flex" justifyContent="space-between" alignItems="center" height={32}>
                  <Typography variant="body2">
                    {t(
                      Resources.AnalysisDetail.Assets.Liabilities.LiabilityTypePlural[
                        LIABILITY_SUBTYPE_RESOURCE_KEY[liability.liability.liabilitySubType]
                      ],
                    )}
                  </Typography>
                  <Box display="flex">
                    <Typography variant="body2">{formatCurrencyWithoutCode(liability.paymentAmount.value)}</Typography>
                    <Typography variant="body2" color={colors.textSecondary}>
                      &nbsp;{t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <NumberField
                    label={t(Resources.AnalysisDetail.Insurance.AdditionalInformation.RemainingPrincipal)}
                    value={liability.liability.amount.value}
                    disabled
                    scale={2}
                    thousandsSeparator=" "
                    unit={t(Resources.Common.CZKSuffix)}
                    fullWidth
                  />
                </Box>
                <Grid container spacing={Spacing.XL}>
                  <Grid size={6}>
                    <LiabilityDueDatePicker
                      dueDate={dueDate}
                      disabled={readOnly}
                      label={t(Resources.AnalysisDetail.Insurance.AdditionalInformation.RepaymentDate)}
                      onChange={async (date) => {
                        const fullItem = liabilityItemsByGuid.get(liability.liability.guid);
                        try {
                          await updateLiability({
                            analysisId,
                            versionId,
                            itemId: liability.liability.guid,
                            data: {
                              name: liability.liability.name,
                              amount: {
                                currencyCode: liability.liability.amount.currencyCode,
                                frequency: AmountFrequency.one_time,
                                value: liability.liability.amount.value,
                              },
                              note: fullItem?.note,
                              institution: fullItem?.institution,
                              clientGuid: liability.liability.clientGuid,
                              relatedExpenseItemGuid: fullItem?.relatedExpenseItemGuid,
                              liabilitySubType: liability.liability.liabilitySubType,
                              dueDate: date ? format(date, "yyyy-MM-dd") : null,
                            },
                          });
                        } catch {
                          toast.error(t(Resources.Common.Error));
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={6}>
                    <Box>
                      <NumberField
                        label={t(Resources.AnalysisDetail.Insurance.AdditionalInformation.RemainingYears)}
                        value={differenceInYears(new Date(dueDate ? dueDate : new Date()), new Date())}
                        disabled
                        scale={2}
                        thousandsSeparator=" "
                        unit={t(Resources.Common.Years)}
                        fullWidth
                      />
                    </Box>
                  </Grid>
                </Grid>
                {index + 1 !== familyLiabilities.length && (
                  <>
                    <Box mb={Spacing.M} />
                    <Divider />
                  </>
                )}
              </React.Fragment>
            );
          })}
        </StyledStack>
      </Stack>
    </Stack>
  );
};
